import React, { useEffect, useState } from "react";
import ProductRows from "./ProductRows";
import { ProductCategory, ProductRow, ProductSelectorProps } from "../../types";
import DashboardDropdown from "@/ReactInit/Scripts/DashboardPage/components/DashboardDropdown";
import {
  cn,
  normalizeProductKey,
} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardHelpers";

const createInitialRows = (count: number): ProductRow[] =>
  Array.from({ length: count }, () => ({
    product: "",
    volume: "",
    lastYearVolume: null,
  }));

const ProductSelector: React.FC<ProductSelectorProps> = ({
  selectedCategory,
  products,
  volumes,
  lastYearVolumes,
  onCategoryChange,
  onProductChange,
  onVolumeChange,
  onLastYearVolumeChange,
  onRowErrorStateChange,
  lastYearVolumesByProduct,
  headerRightSlot,
  disabledCategories = [],
  reportYear,
}) => {
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [localCategory, setLocalCategory] = useState(selectedCategory || "");

  const [categoryProducts, setCategoryProducts] = useState<string[]>([]);
  const [rows, setRows] = useState<ProductRow[]>(createInitialRows(4));
  const [searchInputs, setSearchInputs] = useState<string[]>(Array(4).fill(""));
  const [showLastYear, setShowLastYear] = useState(false);

  const effectiveReportYear = reportYear ?? null;
  const effectiveCompareYear =
    effectiveReportYear != null ? effectiveReportYear - 1 : null;

  // Get categories and products from local json-file
  useEffect(() => {
    fetch("/dashboard/json/productCategories.json")
      .then((res) => res.json())
      .then((data) => setCategories(data))
      .catch((err) =>
        console.error("Kunne ikke hente produktkategorier:", err)
      );
  }, []);

  useEffect(() => {
    if (selectedCategory !== localCategory) {
      setLocalCategory(selectedCategory || "");
    }
  }, [selectedCategory]);

  useEffect(() => {
    const baseRows = products.map((product, idx) => ({
      product,
      volume: volumes[idx] || "",
      lastYearVolume: lastYearVolumes[idx] ?? null,
    }));

    const displayRows =
      baseRows.length < 4
        ? [...baseRows, ...createInitialRows(4 - baseRows.length)]
        : baseRows;

    setRows(displayRows);
    setSearchInputs(displayRows.map((r) => r.product || ""));
  }, [products, volumes, lastYearVolumes]);

  const handleCategorySelect = (category: string) => {
    const next = category?.trim() ? category : "";

    setLocalCategory(next);
    onCategoryChange(next || null);

    const selectedCategoryData = categories.find((c) => c.category === next);
    setCategoryProducts(
      selectedCategoryData ? Object.keys(selectedCategoryData.products) : []
    );

    setRows(createInitialRows(4));
    setSearchInputs(Array(4).fill(""));
  };

  useEffect(() => {
    const selectedCategoryData = categories.find(
      (c) => c.category === localCategory
    );
    setCategoryProducts(
      selectedCategoryData ? Object.keys(selectedCategoryData.products) : []
    );
  }, [localCategory, categories]);

  useEffect(() => {
    setRows((prev) =>
      prev.map((r) => {
        if (!r.product?.trim()) return r;

        const k = normalizeProductKey(r.product);
        const last =
          typeof lastYearVolumesByProduct[k] === "number"
            ? lastYearVolumesByProduct[k]
            : null;

        if (r.lastYearVolume === last) return r;
        return { ...r, lastYearVolume: last };
      })
    );
  }, [lastYearVolumesByProduct]);

  useEffect(() => {
    const anyLastYear = rows.some((r) => r.lastYearVolume != null);
    if (anyLastYear) setShowLastYear(true);
  }, [rows]);

  const hasLastYearForProduct = (product: string): boolean => {
    const key = normalizeProductKey(product);
    return typeof lastYearVolumesByProduct[key] === "number";
  };

  const categoryHasLastYearData = (category: ProductCategory): boolean => {
    const names = Object.keys(category.products ?? {});
    return names.some(hasLastYearForProduct);
  };

  const takenKeys = rows
    .map((r) => r.product)
    .filter((p) => !!p && p.trim())
    .map(normalizeProductKey);

  const isCategoryDisabled =
    !!localCategory && disabledCategories.includes(localCategory);
  const canShowRows = !!localCategory && !isCategoryDisabled;  

  return (
    <div className="flex flex-col gap-6">
      <div
        className={cn(
          "grid grid-cols-1 gap-4",
          headerRightSlot ? "tablet:grid-cols-3" : "tablet:grid-cols-1"
        )}
      >
        <div
          className={cn(
            "min-w-0",
            headerRightSlot ? "tablet:col-span-2" : "tablet:col-span-1"
          )}
        >
          <DashboardDropdown
            label="Produktkategori"
            required
            placeholder="Velg produktkategori"
            selectedValues={localCategory ? [localCategory] : []}
            onChange={(values) => {
              const next = values[0] ?? "";
              handleCategorySelect(next);
            }}
            groups={[
              {
                items: categories.map((c) => {
                  const isDisabled =
                    disabledCategories.includes(c.category) &&
                    c.category !== localCategory;
                  return {
                    value: c.category,
                    label: c.category,
                    disabled: isDisabled,
                    disabledReason: isDisabled
                      ? "Denne produktkategorien er allerede i bruk."
                      : undefined,
                    rightIconClassName: categoryHasLastYearData(c)
                      ? "icon-chart-area"
                      : undefined,
                    rightIconTooltip: categoryHasLastYearData(c)
                      ? "Finnes i tidligere rapport"
                      : undefined,
                  };
                }),
              },
            ]}
            menuClassName="min-w-full laptop:min-w-80 w-fit min-h-72"
          />
        </div>

        {headerRightSlot && (
          <div className="min-w-0 tablet:col-span-1 tablet:flex tablet:justify-end">
            <div className="w-full tablet:max-w-[220px]">{headerRightSlot}</div>
          </div>
        )}
      </div>

      {canShowRows ? (
        <ProductRows
          rows={rows}
          setRows={setRows}
          searchInputs={searchInputs}
          setSearchInputs={setSearchInputs}
          category={localCategory}
          categoryProducts={categoryProducts}
          showLastYear={showLastYear}
          setShowLastYear={setShowLastYear}
          onProductChange={onProductChange}
          onVolumeChange={onVolumeChange}
          onLastYearVolumeChange={onLastYearVolumeChange}
          onRowErrorStateChange={onRowErrorStateChange}
          lastYearVolumesByProduct={lastYearVolumesByProduct}
          takenProducts={takenKeys}
          reportYear={effectiveReportYear ?? undefined}
          compareYear={effectiveCompareYear ?? undefined}
        />
      ) : null}
    </div>
  );
};

export default ProductSelector;
